import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useCourse } from '../contexts/CourseContext';
import {
  BookOpen, Plus, Trash2, Save, ArrowLeft,
  Clock, Users, Award, AlertCircle, Image,
} from 'lucide-react';

const CATEGORIES = ['Développement Web', 'Data Science', 'Marketing', 'Design', 'Business', 'Langues'];
const LEVELS     = ['Débutant', 'Intermédiaire', 'Avancé'];
const LANGUAGES  = ['Français', 'Anglais', 'Espagnol'];

const DIFFICULTY_MAP   = { beginner: 'Débutant', intermediate: 'Intermédiaire', advanced: 'Avancé' };
const DIFFICULTY_UNMAP = { 'Débutant': 'beginner', 'Intermédiaire': 'intermediate', 'Avancé': 'advanced' };

const EditCourse = () => {
  const { courseId } = useParams();
  const navigate     = useNavigate();
  const { user }     = useAuth();
  const { refreshCourses } = useCourse();

  const [loadingCourse, setLoadingCourse] = useState(true);
  const [saving,        setSaving]        = useState(false);
  const [fetchError,    setFetchError]    = useState(null);
  const [saveError,     setSaveError]     = useState(null);
  const [errors,        setErrors]        = useState({});

  const [formData, setFormData] = useState({
    title:           '',
    description:     '',
    thumbnail:       '',
    category:        'Développement Web',
    level:           'Débutant',
    language:        'Français',
    price:           0,
    originalPrice:   0,
    duration:        '',
    certificate:     true,
    requirements:    [''],
    whatYouWillLearn: [''],
  });

  // ── Chargement du cours existant ──────────────────────────────────────────

  useEffect(() => {
    const fetchCourse = async () => {
      setLoadingCourse(true);
      setFetchError(null);
      try {
        const { data } = await axios.get(`/api/courses/${courseId}`);
        const c = data.course;
        setFormData({
          title:            c.title           || '',
          description:      c.description     || '',
          thumbnail:        c.thumbnail       || '',
          category:         c.category        || 'Développement Web',
          level:            DIFFICULTY_MAP[c.difficulty] || 'Débutant',
          language:         c.language        || 'Français',
          price:            c.pricing_price   || 0,
          originalPrice:    c.original_price  || 0,
          duration:         c.estimated_duration ? `${c.estimated_duration} min` : '',
          certificate:      c.certificate     ?? true,
          requirements:     c.requirements?.length    ? c.requirements    : [''],
          whatYouWillLearn: c.what_you_will_learn?.length ? c.what_you_will_learn : [''],
        });
      } catch (err) {
        setFetchError(err.response?.data?.error || err.message);
      } finally {
        setLoadingCourse(false);
      }
    };
    fetchCourse();
  }, [courseId]);

  // ── Accès non autorisé ────────────────────────────────────────────────────

  if (!user || (user.role !== 'instructor' && user.role !== 'admin')) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Accès non autorisé</h1>
          <p className="text-gray-600 mb-6">Vous devez être instructeur ou administrateur.</p>
          <button onClick={() => navigate('/dashboard')} className="bg-primary text-white px-6 py-2 rounded-lg">
            Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleArrayChange = (field, index, value) =>
    setFormData(prev => ({ ...prev, [field]: prev[field].map((item, i) => i === index ? value : item) }));

  const addArrayItem = (field) =>
    setFormData(prev => ({ ...prev, [field]: [...prev[field], ''] }));

  const removeArrayItem = (field, index) => {
    if (formData[field].length > 1)
      setFormData(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim())       newErrors.title       = 'Le titre est requis';
    if (!formData.description.trim()) newErrors.description = 'La description est requise';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setSaveError(null);
    try {
      const durationMin = parseInt(formData.duration) || 0;
      await axios.put(`/api/courses/${courseId}`, {
        title:             formData.title,
        description:       formData.description,
        thumbnail:         formData.thumbnail,
        category:          formData.category,
        difficulty:        DIFFICULTY_UNMAP[formData.level] || 'beginner',
        estimatedDuration: durationMin,
        pricingPrice:      Number(formData.price),
        requirements:      formData.requirements.filter(Boolean),
        whatYouWillLearn:  formData.whatYouWillLearn.filter(Boolean),
      });
      await refreshCourses();
      navigate('/instructor');
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Rendu ─────────────────────────────────────────────────────────────────

  if (loadingCourse) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
          <p className="text-red-600 font-medium mb-4">{fetchError}</p>
          <button onClick={() => navigate('/instructor')} className="text-primary hover:underline text-sm">
            ← Retour
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/instructor')}
            className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au tableau de bord
          </button>
          <h1 className="text-3xl font-bold text-text mb-2">Modifier le cours</h1>
          <p className="text-gray-600">Mettez à jour les informations de votre cours.</p>
        </div>

        {saveError && (
          <div className="flex items-center gap-2 mb-6 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {saveError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">

          {/* Informations de base */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-text mb-6 flex items-center gap-2">
              <BookOpen className="w-5 h-5" /> Informations de base
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Titre du cours *</label>
                <input
                  type="text" name="title" value={formData.title} onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${errors.title ? 'border-red-500' : 'border-gray-300'}`}
                  placeholder="Ex: Développement Web Full-Stack"
                />
                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
                <select name="category" value={formData.category} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Niveau</label>
                <select name="level" value={formData.level} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                  {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Langue</label>
                <select name="language" value={formData.language} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary">
                  {LANGUAGES.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Durée estimée (minutes)</label>
                <input
                  type="number" name="duration" value={formData.duration} onChange={handleChange} min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Ex: 120"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                  <Image className="w-4 h-4" /> Image de couverture (URL)
                </label>
                <div className="flex gap-4 items-start">
                  <input
                    type="url" name="thumbnail" value={formData.thumbnail} onChange={handleChange}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="https://res.cloudinary.com/..."
                  />
                  {formData.thumbnail && (
                    <img
                      src={formData.thumbnail} alt="Aperçu"
                      className="h-16 w-24 object-cover rounded-lg border border-gray-200 flex-shrink-0"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Prix (€)</label>
                <input
                  type="number" name="price" value={formData.price} onChange={handleChange} min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
              <textarea
                name="description" value={formData.description} onChange={handleChange} rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${errors.description ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Décrivez votre cours en détail..."
              />
              {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
            </div>

            <div className="mt-4">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={formData.certificate}
                  onChange={e => setFormData(prev => ({ ...prev, certificate: e.target.checked }))}
                  className="w-4 h-4 text-primary" />
                <span className="text-sm text-gray-700">Certificat de complétion inclus</span>
              </label>
            </div>
          </div>

          {/* Prérequis */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-text mb-6 flex items-center gap-2">
              <Users className="w-5 h-5" /> Prérequis
            </h2>
            <div className="space-y-3">
              {formData.requirements.map((req, i) => (
                <div key={i} className="flex gap-3">
                  <input type="text" value={req}
                    onChange={e => handleArrayChange('requirements', i, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Ex: Aucune expérience requise" />
                  {formData.requirements.length > 1 && (
                    <button type="button" onClick={() => removeArrayItem('requirements', i)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => addArrayItem('requirements')}
                className="flex items-center gap-2 text-primary hover:text-primary/80 text-sm font-medium">
                <Plus className="w-4 h-4" /> Ajouter un prérequis
              </button>
            </div>
          </div>

          {/* Objectifs */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-text mb-6 flex items-center gap-2">
              <Award className="w-5 h-5" /> Ce que vous allez apprendre
            </h2>
            <div className="space-y-3">
              {formData.whatYouWillLearn.map((item, i) => (
                <div key={i} className="flex gap-3">
                  <input type="text" value={item}
                    onChange={e => handleArrayChange('whatYouWillLearn', i, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="Ex: Créer des sites web responsifs" />
                  {formData.whatYouWillLearn.length > 1 && (
                    <button type="button" onClick={() => removeArrayItem('whatYouWillLearn', i)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button type="button" onClick={() => addArrayItem('whatYouWillLearn')}
                className="flex items-center gap-2 text-primary hover:text-primary/80 text-sm font-medium">
                <Plus className="w-4 h-4" /> Ajouter un objectif
              </button>
            </div>
          </div>

          {/* Modules / Leçons */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-text mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5" /> Contenu du cours
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              Pour ajouter ou modifier les modules et leçons, utilisez l'éditeur de contenu.
            </p>
            <button
              type="button"
              onClick={() => navigate(`/instructor/course/${courseId}/content/create`)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              <Plus className="w-4 h-4" /> Ajouter du contenu
            </button>
          </div>

          {/* Actions */}
          <div className="flex gap-4 justify-end">
            <button type="button" onClick={() => navigate('/instructor')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
              Annuler
            </button>
            <button type="submit" disabled={saving}
              className="flex items-center gap-2 bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50">
              {saving ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Sauvegarde...</>
              ) : (
                <><Save className="w-4 h-4" />Enregistrer les modifications</>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCourse;
